import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import GasFormulaContainer from '../containers/GasFormulas';
import RackFormulaContainer from '../containers/RackFormulas';
import BulkFormulaContainer from '../containers/BulkFormulas';
import BiodieselFormulaContainer from '../containers/BiodieselFormulas';
import AuthUser from '../containers/AuthUser';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import DataDumpContainer from '../containers/accounting/DataDump';
import {
	calcNewGasFormula,
	calcRackFormula,
	calcGasFormula,
	calcBulkFormula,
	calcBiodieselFormula,
} from '../libs/formulas';

import Formula from '../components/ui/Formula';

import { getSharedStyles } from '../themes';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PendingIcon from '@material-ui/icons/AccessTime';
import SentIcon from '@material-ui/icons/Send';
import SwipeableViews from 'react-swipeable-views';
import FMPRegionHeader from '../components/ui/FMPRegionHeader';
const styles = (theme) => ({
	...getSharedStyles(theme),
});

function TabContainer({ children, dir }) {
	return (
		<Typography component="div" dir={dir} style={{ padding: 8 * 2 }}>
			{children}
		</Typography>
	);
}
TabContainer.propTypes = {
	children: PropTypes.node.isRequired,
	dir: PropTypes.string.isRequired,
};

class Formulas extends Component {
	constructor(props) {
		super(props);

		const startingTab = _.get(this.props, ['match', 'params', 'startingTab'], 'pending');

		const startingTabIndex =
			{
				pending: 0,
				completed: 1,
			}[startingTab] || 0;

		this.state = {
			tabIndex: startingTabIndex,
			currentData: null,
		};
	}

	componentDidMount() {
		if (!this.props.gasFormulas.length) {
			this.props.searchGasFormulas();
		}
		if (!this.props.rackFormulas.length) {
			this.props.searchRackFormulas();
		}
		if (!this.props.bulkFormulas.length) {
			this.props.searchBulkFormulas();
		}
		if (!this.props.biodieselFormulas.length) {
			this.props.searchBiodieselFormulas();
		}
		this.getCurrentData();
	}

	/**
	 * This should just be used by G&M since they are the only ones that need up-to-date data
	 * Otherwise the data comes from the stationQuotedPrice dataDump
	 */
	getCurrentData = () => {
		const { getCurrentDataDump, showErrors } = this.props;

		getCurrentDataDump({
			callback: (err, r) => {
				if (err) {
					return showErrors([err]);
				}

				//-
				if (!r.data) {
					console.error('Bad result', r);
					return showErrors(['Formula data did not return correctly']);
				}
				const { data } = r;
				this.setState({ currentData: data });
			},
		});
	};

	handleTabChange = (event, tabIndex) => {
		this.setState({ tabIndex });
	};

	handleChangeTabIndex = (index) => {
		this.setState({ tabIndex: index });
	};

	render() {
		const { theme, classes } = this.props;

		if (
			!this.props.gasFormulas ||
			!this.props.rackFormulas ||
			!this.props.bulkFormulas ||
			!this.props.biodieselFormulas ||
			!this.state.currentData
		) {
			return <div>Fetching data...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h5" align="left" gutterBottom>
						Pricing Formulas
					</Typography>

					<AppBar position="static" color="default">
						<Tabs
							value={this.state.tabIndex}
							onChange={this.handleTabChange}
							indicatorColor="primary"
							textColor="primary"
							variant="fullWidth"
							centered
						>
							<Tab label="E85 (new)" icon={<PendingIcon />} />
							<Tab label="E85 (old)" icon={<SentIcon />} />
							<Tab label="Rack (old)" icon={<SentIcon />} />
							<Tab label="Bulk" icon={<SentIcon />} />
							<Tab label="Biodiesel" icon={<SentIcon />} />
						</Tabs>
					</AppBar>

					<Paper className={classes.paper}>
						<SwipeableViews
							axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
							index={this.state.tabIndex}
							onChangeIndex={this.handleChangeTabIndex}
						>
							<TabContainer dir={theme.direction}>{this.renderNewFormulas()}</TabContainer>
							<TabContainer dir={theme.direction}>{this.renderOldE85Formulas()}</TabContainer>
							<TabContainer dir={theme.direction}>{this.renderOldRackFormulas()}</TabContainer>
							<TabContainer dir={theme.direction}>{this.renderBulk()}</TabContainer>
							<TabContainer dir={theme.direction}>{this.renderBiodiesel()}</TabContainer>
						</SwipeableViews>
					</Paper>
				</Grid>
			</Grid>
		);
	}

	renderNewFormulas = () => {
		const { gasFormulas } = this.props;
		const { currentData } = this.state;

		const gasColumnData = [
			{ id: 'name', numeric: false, label: 'Formula Name' },
			{ id: 'calculation', numeric: false, label: 'Pricing' },
		];

		let newRetailFlatFormula = {};
		let newRetailPlus10Formula = {};
		let newRetailMinus10Formula = {};
		const gasRows = [];
		gasFormulas.forEach((row) => {
			const { formulaKey, isNewFormula } = row;
			if (!isNewFormula) {
				return;
			}

			if (formulaKey === 'newRetailFlat') {
				newRetailFlatFormula = row;
			} else if (formulaKey === 'newRetailPlus10') {
				newRetailPlus10Formula = row;
			} else if (formulaKey === 'newRetailMinus10') {
				newRetailMinus10Formula = row;
			}
			gasRows.push(row);
		});

		let newRetailFlatFormulaRows;
		const r = calcNewGasFormula({ data: currentData, formulaKey: 'newRetailFlat' });
		if (r.notImplemented) {
			newRetailFlatFormulaRows = 'n/a';
		} else {
			newRetailFlatFormulaRows = (
				<Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} />
			);
		}

		let newRetailPlus10FormulaRows;
		const r2 = calcNewGasFormula({ data: currentData, formulaKey: 'newRetailPlus10' });
		if (r2.notImplemented) {
			newRetailPlus10FormulaRows = 'n/a';
		} else {
			newRetailPlus10FormulaRows = (
				<Formula formulaRows={r2.formulaRows} formulaNotes={r2.formulaNotes} />
			);
		}

		let newRetailMinus10FormulaRows;
		const r3 = calcNewGasFormula({ data: currentData, formulaKey: 'newRetailMinus10' });
		if (r3.notImplemented) {
			newRetailMinus10FormulaRows = 'n/a';
		} else {
			newRetailMinus10FormulaRows = (
				<Formula formulaRows={r3.formulaRows} formulaNotes={r3.formulaNotes} />
			);
		}

		const style = {
			marginTop: 15,
			fontSize: 24,
			marginBottom: 10,
		};

		return (
			<>
				<FMPRegionHeader isTop={true} label="E85 Pricing Formulas (new)" />
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<div style={style}>
							{newRetailFlatFormula.display}: {newRetailFlatFormula.calculationDisplay}
						</div>
						{newRetailFlatFormulaRows}
					</Grid>
					<Grid item xs={12} md={6}>
						<div style={style}>Full Listing</div>
						<Table size="small">
							<TableHead>
								<TableRow>
									{gasColumnData.map((column) => {
										return (
											<TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
												{column.label}
											</TableCell>
										);
									}, this)}
								</TableRow>
							</TableHead>
							<TableBody>
								{_.sortBy(gasRows, 'calculationDisplay').map((n) => {
									/** @namespace n.calculationDisplay  **/
									const style = { padding: '8px 24px 8px 16px' };
									if (n.formulaKey === 'newRetailFlat') {
										style.fontWeight = '900';
										style.backgroundColor = '#dcefff';
									}
									return (
										<TableRow key={n.gasFormulaId} style={{ height: 'auto' }}>
											<TableCell style={style}>{n.display}</TableCell>
											<TableCell style={style}>{n.calculationDisplay}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Grid>

					<Grid item xs={12} md={12}>
						<FMPRegionHeader isTop={true} label="Pearson Margin / Discount Example" />
					</Grid>
					<Grid item xs={12} md={6}>
						<div style={style}>
							{newRetailPlus10Formula.display}: {newRetailPlus10Formula.calculationDisplay}
						</div>
						{newRetailPlus10FormulaRows}
					</Grid>
					<Grid item xs={12} md={6}>
						<div style={style}>
							{newRetailMinus10Formula.display}: {newRetailMinus10Formula.calculationDisplay}
						</div>
						{newRetailMinus10FormulaRows}
					</Grid>
				</Grid>
			</>
		);
	};

	renderOldE85Formulas = () => {
		const { gasFormulas } = this.props;
		const { currentData } = this.state;

		const gasColumnData = [
			{ id: 'name', numeric: false, label: 'Formula Name' },
			{ id: 'calculation', numeric: false, label: 'Pricing' },
			{ id: 'formulaRows', numeric: false, label: 'Visual Formula' },
		];

		const gasRows = [];
		gasFormulas.forEach((row) => {
			const { formulaKey, isNewFormula } = row;
			if (isNewFormula) {
				return;
			}

			const r = calcRackFormula({ data: currentData, formulaKey });
			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				//row.uiCalculation = 'n/a';
				row.formulaRows = 'n/a';
			} else {
				//row.uiCalculation = _.map(r.priceRows, row => {return _f(row.value);}).join(' / ');
				row.formulaRows = <Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} />;
			}
			gasRows.push(row);
		});

		gasRows.forEach((row) => {
			const { formulaKey } = row;

			let rackFormulaKey = 'pdi';
			if (
				formulaKey === 'falconFormula' ||
				formulaKey === '29Palms' ||
				formulaKey === 'formula15' ||
				formulaKey === 'formula15NoLcfs' ||
				formulaKey === 'formula15LaLow' ||
				formulaKey === 'formula15LaLowNoLcfs' ||
				formulaKey === 'formula15SacLow' ||
				formulaKey === 'formula15SacLowMinus025' ||
				formulaKey === 'fresnoLow' ||
				formulaKey === 'formula15Msd3' ||
				formulaKey === 'h&sE85' ||
				formulaKey === 'h&sNew' ||
				formulaKey === 'au' ||
				formulaKey === 'kaManagementFormula' ||
				formulaKey === 'g&mE85Modified'
			) {
				rackFormulaKey = 'opisFlat';
			}

			if (formulaKey === 'g&mE85') {
				rackFormulaKey = 'gm';
			}

			if (formulaKey === 'boyettE85') {
				rackFormulaKey = 'kinergyForBoyett';
			}

			const r = calcGasFormula({ data: currentData, formulaKey, rackFormulaKey });

			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				//row.uiCalculation = 'n/a';
				row.formulaRows = 'n/a';
			} else {
				//row.uiCalculation = _.map(r.priceRows, row => {return _f(row.value);}).join(' / ');
				row.formulaRows = <Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} />;
			}
		});

		return (
			<>
				<FMPRegionHeader isTop={true} label="E85 Pricing Formulas (old)" />
				<div style={{ fontSize: 15, color: '#666' }}>
					<div>
						<strong>Notes:</strong> Ethanol price based on PDI ethanol cost formula
					</div>
					<div>
						<strong>Exceptions:</strong>
					</div>
					<div>
						Falcon, Formula 15, Formula 15 (Sac Low), Formula 15 (Sac Low Minus .025), Formula 15
						MSD 2, H&mp;S New, H&mp;S (old), and KA Management should always use OPIS Flat
					</div>
					<div>G&mp;M E85 cost should always use the G&mp;M ethanol price</div>
					<div>Boyett E85 cost should always use the Kinergy for Boyett ethanol price</div>
				</div>

				<Table>
					<TableHead>
						<TableRow>
							{gasColumnData.map((column) => {
								return (
									<TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
										{column.label}
									</TableCell>
								);
							}, this)}
						</TableRow>
					</TableHead>
					<TableBody>
						{gasRows.map((n) => {
							/** @namespace n.calculationDisplay  **/
							return (
								<TableRow key={n.gasFormulaId}>
									<TableCell>{n.display}</TableCell>
									<TableCell>{n.calculationDisplay}</TableCell>
									<TableCell>{n.formulaRows}</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</>
		);
	};

	renderOldRackFormulas = () => {
		const { gasFormulas, rackFormulas } = this.props;
		const { currentData } = this.state;

		const rackColumnData = [
			{ id: 'name', numeric: false, label: 'Formula Name' },
			{ id: 'calculation', numeric: false, label: 'Current / Proj Monthly / Proj Avg' },
			{ id: 'formulaRows', numeric: false, label: 'Visual Formula' },
		];

		const rackRows = rackFormulas;
		const gasRows = gasFormulas;

		rackRows.forEach((row) => {
			const { formulaKey } = row;
			const r = calcRackFormula({ data: currentData, formulaKey });
			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				//row.uiCalculation = 'n/a';
				row.formulaRows = 'n/a';
			} else {
				//row.uiCalculation = _.map(r.priceRows, row => {return _f(row.value);}).join(' / ');
				row.formulaRows = <Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} />;
			}
		});

		gasRows.forEach((row) => {
			const { formulaKey } = row;

			let rackFormulaKey = 'pdi';
			if (
				formulaKey === 'falconFormula' ||
				formulaKey === '29Palms' ||
				formulaKey === 'formula15' ||
				formulaKey === 'formula15NoLcfs' ||
				formulaKey === 'formula15LaLow' ||
				formulaKey === 'formula15LaLowNoLcfs' ||
				formulaKey === 'formula15SacLow' ||
				formulaKey === 'formula15SacLowMinus025' ||
				formulaKey === 'fresnoLow' ||
				formulaKey === 'formula15Msd3' ||
				formulaKey === 'h&sE85' ||
				formulaKey === 'h&sNew' ||
				formulaKey === 'au' ||
				formulaKey === 'kaManagementFormula' ||
				formulaKey === 'g&mE85Modified'
			) {
				rackFormulaKey = 'opisFlat';
			}

			if (formulaKey === 'g&mE85') {
				rackFormulaKey = 'gm';
			}

			if (formulaKey === 'boyettE85') {
				rackFormulaKey = 'kinergyForBoyett';
			}

			const r = calcGasFormula({ data: currentData, formulaKey, rackFormulaKey });

			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				//row.uiCalculation = 'n/a';
				row.formulaRows = 'n/a';
			} else {
				//row.uiCalculation = _.map(r.priceRows, row => {return _f(row.value);}).join(' / ');
				row.formulaRows = <Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} />;
			}
		});

		return (
			<>
				<FMPRegionHeader isTop={true} label="Ethanol Rack Pricing Formulas (old)" />
				<Table>
					<TableHead>
						<TableRow>
							{rackColumnData.map((column) => {
								return (
									<TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
										{column.label}
									</TableCell>
								);
							}, this)}
						</TableRow>
					</TableHead>
					<TableBody>
						{rackRows.map((n) => {
							return (
								<TableRow key={n.rackFormulaId}>
									<TableCell>{n.display}</TableCell>
									<TableCell>
										{n.laEth} / {n.projectedMonthlyLaEth} / {n.upToDateMonthlyLaEth}
									</TableCell>
									<TableCell>{n.formulaRows}</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</>
		);
	};

	renderBulk = () => {
		const { bulkFormulas } = this.props;
		const { currentData } = this.state;
		const bulkColumnData = [
			{ id: 'name', numeric: false, label: 'Formula Name' },
			{ id: 'calculation', numeric: false, label: 'Pricing' },
			{ id: 'formulaRows', numeric: false, label: 'Visual Formula' },
		];

		const bulkRows = bulkFormulas;
		bulkRows.forEach((row) => {
			const { bulkFormulaKey } = row;

			const r = calcBulkFormula({ bulkFormulaKey, data: currentData });

			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				row.formulaRows = 'n/a';
			} else {
				row.formulaRows = (
					<Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} isBulk={true} />
				);
			}
		});

		return (
			<>
				<FMPRegionHeader isTop={true} label="Bulk Cost Formulas" />
				<Table>
					<TableHead>
						<TableRow>
							{bulkColumnData.map((column) => {
								return <TableCell key={column.id}>{column.label}</TableCell>;
							}, this)}
						</TableRow>
					</TableHead>
					<TableBody>
						{bulkRows.map((n) => {
							return (
								<TableRow key={n.bulkFormulaId}>
									<TableCell>{n.display}</TableCell>
									<TableCell>{n.calculationDisplay}</TableCell>
									<TableCell>{n.formulaRows}</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</>
		);
	};

	renderBiodiesel = () => {
		const { biodieselFormulas } = this.props;
		const { currentData } = this.state;

		const biodieselColumnData = [
			{ id: 'name', numeric: false, label: 'Formula Name' },
			{ id: 'calculation', numeric: false, label: 'Pricing' },
			{ id: 'formulaRows', numeric: false, label: 'Visual Formula' },
		];

		const biodieselRows = biodieselFormulas;

		biodieselRows.forEach((row) => {
			const { biodieselFormulaKey } = row;
			const r = calcBiodieselFormula({ biodieselFormulaKey, data: currentData });

			/** @namespace r.notImplemented **/
			if (r.notImplemented) {
				row.formulaRows = 'n/a';
			} else {
				row.formulaRows = (
					<Formula formulaRows={r.formulaRows} formulaNotes={r.formulaNotes} isBiodiesel={true} />
				);
			}
		});

		return (
			<>
				<FMPRegionHeader isTop={true} label="Biodiesel Cost Formulas" />
				<Table>
					<TableHead>
						<TableRow>
							{biodieselColumnData.map((column) => {
								return <TableCell key={column.id}>{column.label}</TableCell>;
							}, this)}
						</TableRow>
					</TableHead>
					<TableBody>
						{biodieselRows.map((n) => {
							/** @namespace n.biodieselFormulaId **/
							return (
								<TableRow key={n.biodieselFormulaId}>
									<TableCell>{n.display}</TableCell>
									<TableCell>{n.calculationDisplay}</TableCell>
									<TableCell>{n.formulaRows}</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</>
		);
	};
}

Formulas.propTypes = {
	classes: PropTypes.object.isRequired,
	theme: PropTypes.object.isRequired,
	searchGasFormulas: PropTypes.func.isRequired,
	searchRackFormulas: PropTypes.func.isRequired,
	searchBulkFormulas: PropTypes.func.isRequired,
	searchBiodieselFormulas: PropTypes.func.isRequired,

	gasFormulas: PropTypes.array,
	rackFormulas: PropTypes.array,
	bulkFormulas: PropTypes.array,
	biodieselFormulas: PropTypes.array,

	showErrors: PropTypes.func.isRequired,
	getCurrentDataDump: PropTypes.func.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	BulkFormulaContainer,
	BiodieselFormulaContainer,
	GasFormulaContainer,
	RackFormulaContainer,
	AuthUser,
	DataDumpContainer,
])(Formulas);
